/* eslint-disable @typescript-eslint/no-explicit-any -- SDK generics are deliberately broad */
import { useEffect, useMemo, useRef } from 'react';
import type {
  DbConnectionBuilder,
  DbConnectionImpl,
  ManagedConnectionState,
} from 'spacetimedb';
import { ConnectionManager } from 'spacetimedb';
import { getReconnectThrottle } from './reconnectThrottle';

/**
 * Reconnect a pool entry when `state.connectionError` is set.
 *
 * Exponential backoff (2s → 60s) for the first `maxAttempts` retries,
 * then keepalive cadence at `maxDelayMs` indefinitely — a self-healing
 * server eventually gets a reconnect without the user reloading.
 * `accessDeniedDelayMs` is a separate longer floor so server-side role
 * propagation has time to land before we keep hammering.
 *
 * Two robustness properties:
 *
 *   - **Never gives up permanently.** After the fast attempts are spent
 *     we keep retrying at `maxDelayMs` cadence. `onGiveUp` is purely a
 *     telemetry hook (called once when the fast phase ends).
 *   - **Wakes on tab visible / network online.** A backgrounded tab
 *     freezes our setTimeout backoff and AuthKit's silent token
 *     refresh; a socket dropped while hidden would otherwise sit dead
 *     until the next foreground render. The wake path discards the
 *     stale backoff and force-reconnects any module that's currently
 *     down, refreshing the JWT first.
 *
 * Does NOT drive the initial connection — that's owned by the provider.
 */
export type AutoReconnectOptions = {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  accessDeniedDelayMs?: number;
  stabilityThresholdMs?: number;
  isFatal?: (err: Error) => boolean;
  /** Called once when the fast attempts are exhausted (telemetry only —
   *  reconnects continue on the slow cadence). */
  onGiveUp?: (err: Error) => void;
  onReconnect?: () => void;
  /** Awaited before every rebuild. Use it to refresh the JWT so a
   *  reconnect after a backgrounded tab doesn't fire with the stale
   *  token that dropped the socket. Return value is ignored. */
  beforeReconnect?: () => Promise<unknown> | unknown;
};

const DEFAULTS = {
  maxAttempts: 10,
  initialDelayMs: 2_000,
  maxDelayMs: 60_000,
  accessDeniedDelayMs: 30_000,
  stabilityThresholdMs: 30_000,
};

const WAKE_DEDUPE_MS = 3_000;

/**
 * Window event that asks every mounted `useAutoReconnect` to re-attempt its
 * module now if it is down — the same path as tab-visible / network-online.
 * Fired by the connection banner's Retry and by `useEnsureConnection` when a
 * save meets a dead socket (#243).
 */
export const RECONNECT_NOW_EVENT = 'portal:reconnect-now';

export function requestReconnectNow(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(RECONNECT_NOW_EVENT));
}

export function useAutoReconnect<T extends DbConnectionImpl<any>>(
  poolKey: string,
  state: ManagedConnectionState,
  builderFactory: () => DbConnectionBuilder<T>,
  options: AutoReconnectOptions = {},
): { attempts: number; exhausted: boolean } {
  const opts = { ...DEFAULTS, ...options };

  const factoryRef = useRef(builderFactory);
  factoryRef.current = builderFactory;

  const callbacksRef = useRef({
    isFatal: options.isFatal,
    onGiveUp: options.onGiveUp,
    onReconnect: options.onReconnect,
    beforeReconnect: options.beforeReconnect,
  });
  callbacksRef.current = {
    isFatal: options.isFatal,
    onGiveUp: options.onGiveUp,
    onReconnect: options.onReconnect,
    beforeReconnect: options.beforeReconnect,
  };

  const stateRef = useRef(state);
  stateRef.current = state;

  const throttle = useMemo(() => getReconnectThrottle(poolKey), [poolKey]);

  const stabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable-connection reset: after `stabilityThresholdMs` of healthy
  // operation, clear the attempt counter so the next failure starts
  // fresh from the bottom of the backoff curve.
  useEffect(() => {
    if (state.isActive && !state.connectionError) {
      throttle.everActive = true;
      throttle.exhausted = false;
      throttle.fatal = false;
      if (stabilityTimerRef.current) clearTimeout(stabilityTimerRef.current);
      stabilityTimerRef.current = setTimeout(() => {
        throttle.attempts = 0;
      }, opts.stabilityThresholdMs);
      return () => {
        if (stabilityTimerRef.current) {
          clearTimeout(stabilityTimerRef.current);
          stabilityTimerRef.current = null;
        }
      };
    }
  }, [throttle, state.isActive, state.connectionError, opts.stabilityThresholdMs]);

  // Error path: schedule a rebuild with backoff. The SDK has a small
  // race where `onclose` emits before `isActive` flips, so
  // `connectionError` is the source of truth here, not `!isActive`.
  useEffect(() => {
    const err = state.connectionError;
    if (!err) return;
    if (reconnectTimerRef.current) return;
    if (throttle.fatal) return;

    if (callbacksRef.current.isFatal?.(err)) {
      throttle.fatal = true;
      callbacksRef.current.onGiveUp?.(err);
      return;
    }

    const accessDenied = err.message?.includes('Access denied') ?? false;
    const exhausted = !accessDenied && throttle.attempts >= opts.maxAttempts;

    if (exhausted && !throttle.exhausted) {
      throttle.exhausted = true;
      callbacksRef.current.onGiveUp?.(err);
    }

    const delay = accessDenied
      ? opts.accessDeniedDelayMs
      : exhausted
        ? opts.maxDelayMs
        : Math.min(
            opts.maxDelayMs,
            opts.initialDelayMs * Math.pow(2, throttle.attempts),
          );

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      throttle.attempts += 1;
      void (async () => {
        await callbacksRef.current.beforeReconnect?.();
        if (throttle.fatal) return;
        const rebuilt = ConnectionManager.rebuild<T>(
          poolKey,
          factoryRef.current(),
        );
        if (rebuilt) callbacksRef.current.onReconnect?.();
      })();
      // Equal jitter (50–100%), as in the TurboTwin webclient (#243): one edge
      // event drops every module socket at once, and they all share this
      // backoff schedule; without jitter each retry wave hits the rate-limited
      // websocket-token endpoint in lockstep.
    }, delay * (0.5 + Math.random() * 0.5));

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [
    throttle,
    poolKey,
    state.isActive,
    state.connectionError,
    opts.maxAttempts,
    opts.initialDelayMs,
    opts.maxDelayMs,
    opts.accessDeniedDelayMs,
  ]);

  // Wake path: foreground / online → immediately re-attempt anything
  // that's currently down, discarding the (possibly frozen) backoff.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const wake = () => {
      if (throttle.fatal) return;
      const st = stateRef.current;
      if (st.isActive && !st.connectionError) return;
      const isDown =
        !!st.connectionError || (throttle.everActive && !st.isActive);
      if (!isDown) return;

      const now = Date.now();
      if (now - throttle.lastWakeAt < WAKE_DEDUPE_MS) return;
      throttle.lastWakeAt = now;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      throttle.attempts = 0;
      throttle.exhausted = false;
      void (async () => {
        // Stagger 0–1500 ms: the wake fires for EVERY down module at the same
        // instant, and this path has no backoff at all (webclient parity).
        await new Promise((r) => setTimeout(r, Math.random() * 1500));
        await callbacksRef.current.beforeReconnect?.();
        if (throttle.fatal) return;
        const st2 = stateRef.current;
        if (st2.isActive && !st2.connectionError) return;
        const rebuilt = ConnectionManager.rebuild<T>(
          poolKey,
          factoryRef.current(),
        );
        if (rebuilt) callbacksRef.current.onReconnect?.();
      })();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') wake();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', wake);
    window.addEventListener(RECONNECT_NOW_EVENT, wake);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', wake);
      window.removeEventListener(RECONNECT_NOW_EVENT, wake);
    };
  }, [throttle, poolKey]);

  return { attempts: throttle.attempts, exhausted: throttle.exhausted };
}
